import { ApiFilled, MailOutlined } from "@ant-design/icons";
import { DOCS_URLS, useAuthFetch } from "@p0security/frontend-core";
import { useFirestoreDoc } from "@p0security/frontend-core/react";
import { EMAIL_INTEGRATION_KEY } from "@p0security/integrations-email-shared";
import { IntegrationConfig } from "@p0security/shared/integrations/shared";
import { Alert, Button, Space, Spin, Typography } from "@p0security/ui";
import { Tenant } from "components/Login";
import { useCallback, useContext, useMemo, useState } from "react";

import { IntegrationCard } from "../IntegrationCard";

export const Email = () => {
  const tenantId = useContext(Tenant);

  const [error, setError] = useState<string | undefined>();
  const [fetching, setFetching] = useState(false);

  const authFetch = useAuthFetch(setError, setFetching);

  const path = `o/${tenantId}/integrations/email`;
  const config = useFirestoreDoc<IntegrationConfig>(path, {
    live: true,
  });

  const loading = useMemo(
    () => config.loading || fetching,
    [config.loading, fetching]
  );

  const isInstalled = useMemo(
    () => config.doc?.data?.state === "installed",
    [config.doc?.data?.state]
  );

  const canRemove = useMemo(() => {
    return !loading && isInstalled;
  }, [isInstalled, loading]);

  const onRemove = useCallback(async () => {
    await authFetch("integrations/email/installation", {
      method: "DELETE",
    });
  }, [authFetch]);

  const handleInstall = useCallback(async () => {
    await authFetch("integrations/email/installation", {
      method: "POST",
    });
  }, [authFetch]);

  return (
    <IntegrationCard
      canRemove={canRemove}
      onRemove={onRemove}
      integrationDoc={config.doc}
      title="Email"
      integration={EMAIL_INTEGRATION_KEY}
      logo={<MailOutlined />}
      showBreadcrumbs={true}
    >
      <Space direction="vertical">
        {loading ? (
          <Spin />
        ) : error ? (
          <Alert type="error" message={error} />
        ) : isInstalled ? (
          <Alert
            type="success"
            message={
              <>
                Email integration is active. Users will automatically receive
                notifications for pending and approved requests.
                <br />
                <br />
                <Typography.Link
                  href={DOCS_URLS.EMAIL_INSTALL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Read the documentation
                </Typography.Link>
              </>
            }
          />
        ) : (
          <>
            <Typography.Paragraph>
              Enable email notifications to automatically notify requestors and
              approvers about access requests, approvals, revocations, and
              expiration reminders.
            </Typography.Paragraph>
            <Typography.Paragraph type="secondary">
              No additional configuration required.
            </Typography.Paragraph>
            <Button icon={<ApiFilled />} onClick={handleInstall} type="primary">
              Install integration
            </Button>
          </>
        )}
      </Space>
    </IntegrationCard>
  );
};
